// components/VendorMainLayout.js
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Button, Offcanvas } from "react-bootstrap";
import { BsBellFill, BsList } from "react-icons/bs";
import API from "../services/api";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import GstApiDueNotice from "./GstApiDueNotice";
import {
  clearStoredSession,
  getStoredUser,
  redirectToLogin,
  SESSION_USER_UPDATED_EVENT,
  updateStoredUser,
} from "../utils/sessionUser";
import "./PanelMobileChrome.css";

const VendorMainLayout = () => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const onboardingStatus = String(user?.onboardingStatus || "");
  const isFullyApproved =
    onboardingStatus === "approved" ||
    (!onboardingStatus && user?.approved === true);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isFullyApproved) {
        setNotificationCount(0);
        return;
      }
      try {
        const res = await API.get("/vendor/notifications");
        const payload = res.data;
        const nextCount = Array.isArray(payload)
          ? payload.length
          : Number(payload?.count ?? payload?.unreadCount ?? payload?.unread ?? 0);
        setNotificationCount(Number.isFinite(nextCount) ? nextCount : 0);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };

    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 15000);
    return () => window.clearInterval(intervalId);
  }, [isFullyApproved]);

  useEffect(() => {
    const syncAuthUser = async () => {
      try {
        const res = await API.get("/auth/me");
        const nextUser = res?.data || {};
        updateStoredUser(nextUser);
        setUser((prev) => ({ ...prev, ...nextUser }));
      } catch (_e) {}
    };
    syncAuthUser();
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    window.addEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    if (isFullyApproved) return;
    const isAllowed =
      currentPath === "/vendor" ||
      currentPath === "/vendor/" ||
      currentPath === "/vendor/overview" ||
      currentPath === "/vendor/profile";
    if (!isAllowed) {
      navigate("/vendor/overview", { replace: true });
    }
  }, [isFullyApproved, currentPath, navigate]);

  const handleLogout = () => {
    clearStoredSession();
    redirectToLogin();
  };

  const routeTitles = {
    "/vendor/overview": "Dashboard",
    "/vendor/profile": "Profile",
    "/vendor/requirement/history": "Requirement History",
    "/vendor/dispatches": "Dispatch Tracking",
    "/vendor/requirement": "Requirement",
    "/vendor/certificate": "Certificate",
    "/vendor/gallery": "Product Gallery",
    "/vendor/payment-history": "Payment History",
    "/vendor/thumb-ledger": "Thumb Ledger & History",
    "/vendor/notifications": "Notifications",
  };

  const menuItems = [
    { path: "/vendor/overview", label: "Dashboard", icon: "space_dashboard" },
    { path: "/vendor/profile", label: "Profile", icon: "account_circle" },
    { path: "/vendor/requirement", label: "Requirement", icon: "checklist" },
    { path: "/vendor/requirement/history", label: "Requirement History", icon: "history" },
    { path: "/vendor/dispatches", label: "Dispatch Tracking", icon: "local_shipping" },
    { path: "/vendor/gallery", label: "Product Gallery", icon: "photo_library" },
    { path: "/vendor/payment-history", label: "Payment History", icon: "receipt_long" },
    { path: "/vendor/thumb-ledger", label: "Thumb Ledger & History", icon: "fingerprint" },
    { path: "/vendor/certificate", label: "Certificate", icon: "workspace_premium" },
    { path: "/vendor/notifications", label: "Notifications", icon: "notifications" },
  ];
  const visibleMenuItems = isFullyApproved
    ? menuItems
    : menuItems.filter((item) => item.path === "/vendor/overview" || item.path === "/vendor/profile");

  const handleNav = (path) => {
    navigate(path);
    setSidebarVisible(false);
  };

  const isDashboardRoute =
    currentPath === "/vendor" ||
    currentPath === "/vendor/" ||
    currentPath === "/vendor/overview";
  const isBottomActive = (prefixes = [], options = {}) =>
    prefixes.some(
      (p) =>
        currentPath === p || (!options.exact && currentPath.startsWith(`${p}/`))
    );
  const matchedTitlePath = Object.keys(routeTitles)
    .filter((path) => currentPath === path || currentPath.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  const currentTitle = matchedTitlePath ? routeTitles[matchedTitlePath] : "";
  const handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/vendor/overview");
  };

  const userName = user?.fullName || "Vendor";
  const userInitials = useMemo(
    () =>
      String(userName)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("") || "V",
    [userName]
  );
  const userPhoto = resolveProfilePhoto(
    user?.photo,
    user?.photoUpdatedAt || user?.updatedAt || ""
  );

  return (
    <div
      className="panel-layout-shell"
      style={{
        display: "flex",
        height: "100vh",
        "--role-sidebar-gradient": "linear-gradient(180deg, #9a3412 0%, #f59e0b 100%)",
        "--role-sidebar-border": "#7c2d12",
        "--role-title-color": "#c2410c",
        "--panel-accent-rgb": "249, 115, 22",
        "--panel-nav-bg": "#9a3412",
        "--panel-nav-border": "rgba(255, 255, 255, 0.16)",
        "--panel-nav-shadow": "0 -10px 30px rgba(154, 52, 18, 0.35)",
        "--panel-nav-active-bg": "#ffffff",
        "--panel-nav-active-color": "#9a3412",
        "--panel-nav-active-shadow": "0 2px 8px rgba(154, 52, 18, 0.25)",
      }}
    >
      {/* Sidebar (desktop) */}
      <div
        className="d-none d-md-block"
        style={{ width: "240px", background: "var(--role-sidebar-gradient)", height: "100vh", display: "flex", flexDirection: "column" }}
      >
        {/* ✅ Branding Header */}
        <div
          style={{
            color: "white",
            fontSize: "22px",
            fontWeight: "bold",
            padding: "8.5px",
            borderBottom: "2px solid var(--role-sidebar-border)",
            textAlign: "center",
            letterSpacing: "1px",
          }}
        >
          🌾 Lotus Rice Mill Pvt. Ltd.
        </div>

        {/* Sidebar Menu */}
        <div className="d-flex flex-column p-3" style={{ flex: 1, overflowY: "auto" }}>
          <div className="panel-sidebar-menu">
          {visibleMenuItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`panel-sidebar-item ${
                isBottomActive([item.path], { exact: item.path === "/vendor/requirement" })
                  ? "active"
                  : ""
              }`}
              onClick={() => handleNav(item.path)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Sidebar Offcanvas (mobile) */}
      <Offcanvas
        show={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="panel-mobile-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>🌾 Lotus Rice Mill Pvt. Ltd.</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: "linear-gradient(180deg, #9a3412 0%, #f59e0b 100%)", color: "#fff" }}>
          <div className="panel-sidebar-menu">
            {visibleMenuItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${
                  isBottomActive([item.path], { exact: item.path === "/vendor/requirement" })
                    ? "active"
                    : ""
                }`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Topbar + Content */}
      <div className="panel-layout-maincol" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* TOPBAR */}
        <div
          className="panel-layout-topbar"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            backgroundColor: "var(--app-topbar)",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {!isDashboardRoute && (
              <Button
                variant="light"
                className="d-md-none"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <span className="material-symbols-outlined">arrow_back_ios_new</span>
              </Button>
            )}
            <Button
              variant="light"
              className="d-md-none"
              onClick={() => setSidebarVisible(true)}
            >
              <BsList />
            </Button>
            <div
              className="panel-mobile-title"
              style={{ fontSize: "18px", fontWeight: 600, color: "var(--role-title-color)" }}
            >
              {currentTitle}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            {isFullyApproved ? (
              <div
                className="panel-top-notif"
                style={{ position: "relative", cursor: "pointer" }}
                onClick={() => navigate("/vendor/notifications")}
              >
                <BsBellFill size={20} className="text-secondary" />
                {notificationCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-10px",
                      background: "red",
                      color: "white",
                      fontSize: "10px",
                      borderRadius: "50%",
                      padding: "2px 6px",
                    }}
                  >
                    {notificationCount}
                  </span>
                )}
              </div>
            ) : null}

            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                style={{
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt={userName}
                    style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }}
                  />
                ) : (
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#e2e8f0",
                      color: "#334155",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {userInitials}
                  </span>
                )}
                <span className="panel-mobile-user-name">{userName}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item href="/vendor/profile">Profile</Dropdown.Item>
                <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <GstApiDueNotice />

        {/* CONTENT */}
        <div
          className="panel-layout-content"
          style={{
            flex: 1,
            overflowY: "auto",
            background: "var(--app-bg)",
            padding: "20px",
          }}
        >
          <Outlet />
        </div>

        <div className="panel-mobile-nav" style={{ "--panel-nav-count": isFullyApproved ? 7 : 2 }}>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/vendor/overview"]) ? "active" : ""}`}
            onClick={() => navigate("/vendor/overview")}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Dashboard</span>
          </button>
          {isFullyApproved ? (
            <>
              <button
                className={`panel-mobile-nav-item ${
                  isBottomActive(["/vendor/requirement"], { exact: true }) ? "active" : ""
                }`}
                onClick={() => navigate("/vendor/requirement")}
              >
                <span className="material-symbols-outlined">inventory_2</span>
                <span>Req</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/vendor/requirement/history"]) ? "active" : ""}`}
                onClick={() => navigate("/vendor/requirement/history")}
              >
                <span className="material-symbols-outlined">history</span>
                <span>History</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/vendor/dispatches"]) ? "active" : ""}`}
                onClick={() => navigate("/vendor/dispatches")}
              >
                <span className="material-symbols-outlined">local_shipping</span>
                <span>Dispatch</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/vendor/payment-history"]) ? "active" : ""}`}
                onClick={() => navigate("/vendor/payment-history")}
              >
                <span className="material-symbols-outlined">receipt_long</span>
                <span>Payments</span>
              </button>
              <button
                className={`panel-mobile-nav-item ${isBottomActive(["/vendor/thumb-ledger"]) ? "active" : ""}`}
                onClick={() => navigate("/vendor/thumb-ledger")}
              >
                <span className="material-symbols-outlined">fingerprint</span>
                <span>Thumb</span>
              </button>
            </>
          ) : null}
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/vendor/profile"]) ? "active" : ""}`}
            onClick={() => navigate("/vendor/profile")}
          >
            <span className="material-symbols-outlined">person</span>
            <span>Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorMainLayout;
